import React, { useState, useEffect } from 'react';
import { useAppContext } from '../context/AppContext';
import SampleConceptViewer from './SampleConceptViewer';
import SampleFeasibilityViewer from './SampleFeasibilityViewer';
import PricingTableModal from './PricingTableModal';

// Update banner config - set expiry date (banner auto-hides after this date)
const BANNER_EXPIRY_DATE = new Date('2026-01-21T23:59:59');
const BANNER_STORAGE_KEY = 'planai_update_banner_dismissed';

const LandingPage = () => {
  const { 
    setAuthMode, 
    setShowAuthModal,
    showSampleViewer,
    setShowSampleViewer,
    showFeasibilityViewer,
    setShowFeasibilityViewer
  } = useAppContext();

  const [showPricingTable, setShowPricingTable] = useState(false);
  const [showUpdateBanner, setShowUpdateBanner] = useState(false);

  // Check if banner should be shown
  useEffect(() => {
    const now = new Date();
    const isDismissed = localStorage.getItem(BANNER_STORAGE_KEY) === 'true';
    const isExpired = now > BANNER_EXPIRY_DATE;
    
    if (!isDismissed && !isExpired) {
      setShowUpdateBanner(true);
    }
  }, []);

  const handleDismissBanner = () => {
    localStorage.setItem(BANNER_STORAGE_KEY, 'true');
    setShowUpdateBanner(false);
  };

  const handlePrivacyClick = (e) => {
    e.preventDefault();
    window.location.href = '/privacy';
  };

  const handleTermsClick = (e) => {
    e.preventDefault();
    window.location.href = '/terms';
  };

  // Update Banner Component
  const UpdateBanner = () => (
    <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 py-3 relative">
      <div className="max-w-6xl mx-auto flex items-center justify-center gap-3">
        <span className="text-lg">✨</span>
        <p className="text-sm font-medium">
          Website and features have been updated
        </p>
        <button
          onClick={handleDismissBanner}
          className="absolute right-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white transition-colors p-1"
          aria-label="Dismiss banner"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );

  const Hero = () => (
    <section 
      className="relative min-h-[75vh] flex items-center"
      style={{
        backgroundImage: `linear-gradient(135deg, rgba(15, 23, 42, 0.80) 0%, rgba(30, 64, 175, 0.65) 50%, rgba(15, 23, 42, 0.75) 100%), url('https://images.unsplash.com/photo-1512917774080-9991f1c4c750?ixlib=rb-4.0.3&auto=format&fit=crop&w=2071&q=80')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      <div className="max-w-6xl mx-auto px-8 py-20 w-full">
        <div className="max-w-2xl">
          {/* Logo */}
          <h1 className="text-5xl font-bold text-white mb-6">
            <span className="font-light">plan</span>
            <span className="text-yellow-400">AI</span>
            <span className="font-light">design</span>
          </h1>
          
          {/* Main headline */}
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 leading-tight">
            Find out what you can build on your block
          </h2>
          
          <p className="text-xl text-gray-300 mb-8 leading-relaxed">
            Fast, clearly priced desktop feasibility and design for standard NSW residential sites — single dwellings, duplexes, townhouses, secondary dwellings, and alterations and additions. Prepared by qualified professionals, delivered online.
          </p>

          {/* Trust chips */}
          <div className="flex flex-wrap gap-3 mb-8">
            <span className="inline-flex items-center gap-2 bg-white/10 text-white px-3 py-1.5 rounded-full text-sm">
              <span className="text-green-400">✓</span> Pricing from $149
            </span>
            <span className="inline-flex items-center gap-2 bg-white/10 text-white px-3 py-1.5 rounded-full text-sm">
              <span className="text-green-400">✓</span> Set revisions included
            </span>
            <span className="inline-flex items-center gap-2 bg-white/10 text-white px-3 py-1.5 rounded-full text-sm">
              <span className="text-green-400">✓</span> See a real sample first
            </span>
          </div>

          {/* Hero CTAs */}
          <div className="flex flex-wrap gap-4">
            <button
              onClick={() => setShowFeasibilityViewer(true)}
              className="bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors shadow-lg"
            >
              See a sample feasibility report
            </button>
            <button
              onClick={() => { setAuthMode('register'); setShowAuthModal(true); }}
              className="bg-transparent border-2 border-white/60 text-white hover:bg-white/10 px-8 py-4 rounded-lg font-semibold text-lg transition-colors"
            >
              Start your project — from $149
            </button>
          </div>
        </div>
      </div>
    </section>
  );

  const HowItWorks = () => (
    <section 
      className="py-16 relative"
      style={{
        backgroundImage: `linear-gradient(to bottom, rgba(241, 245, 249, 0.82) 0%, rgba(241, 245, 249, 0.82) 100%), url('https://images.unsplash.com/photo-1503387762-592deb58ef4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=2831&q=80')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      <div className="max-w-6xl mx-auto px-8">
        <p className="text-sm font-semibold text-blue-600 uppercase tracking-wider mb-2 text-center">How It Works</p>
        <h2 className="text-3xl font-bold text-center mb-12 text-slate-800">Simple, streamlined process</h2>
        
        <div className="grid md:grid-cols-3 gap-8">
          {/* Card 1 - Online */}
          <div className="text-center bg-white p-6 rounded-xl shadow-sm">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-slate-800 mb-2">Convenient Online Process</h3>
            <p className="text-sm text-slate-600 leading-relaxed">
              Submit your project details through our portal. Most standard projects can be completed without meetings or site visits. Complex sites may require inspection.
            </p>
          </div>

          {/* Card 2 - Professional */}
          <div className="text-center bg-white p-6 rounded-xl shadow-sm">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-slate-800 mb-2">Professional Team</h3>
            <p className="text-sm text-slate-600 leading-relaxed">
              All work prepared by qualified professionals with extensive NSW council approval experience.
            </p>
          </div>

          {/* Card 3 - Platform */}
          <div className="text-center bg-white p-6 rounded-xl shadow-sm">
            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-slate-800 mb-2">All-in-One Platform</h3>
            <p className="text-sm text-slate-600 leading-relaxed">
              Communication, payments, and deliverables — all managed securely through this platform.
            </p>
          </div>
        </div>
      </div>
    </section>
  );

  const Services = () => (
    <section 
      className="py-16"
      style={{
        backgroundImage: `linear-gradient(to bottom, rgba(226, 232, 240, 0.82) 0%, rgba(226, 232, 240, 0.82) 100%), url('https://images.unsplash.com/photo-1503387762-592deb58ef4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=2831&q=80')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      <div className="max-w-6xl mx-auto px-8">
        <p className="text-sm font-semibold text-green-600 uppercase tracking-wider mb-2 text-center">Our Services</p>
        <h2 className="text-3xl font-bold text-center mb-4 text-slate-800">Two stages to get you started</h2>
        <p className="text-center text-slate-600 mb-3 max-w-2xl mx-auto">We recommend starting with Stage 1 Feasibility to understand your site's potential and constraints before moving to Concept Design.</p>
        <p className="text-center text-slate-500 text-sm mb-10 max-w-2xl mx-auto italic">Note: If Stage 1 is bypassed, feasibility assessment is integrated into the Stage 2 process.</p>
        
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Stage 1 Card */}
          <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100 flex flex-col">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-3 py-1 rounded-full uppercase tracking-wide">Stage 1</span>
              <span className="text-2xl font-bold text-slate-800">$149<span className="text-sm font-normal text-slate-500">+</span></span>
            </div>
            <h3 className="text-lg font-bold text-slate-800 mb-2">Site Investigation & Feasibility</h3>
            <p className="text-sm text-slate-600 mb-4 leading-relaxed flex-grow">
              Comprehensive desktop site analysis and development feasibility assessment to determine your property's potential for residential development.
            </p>
            <div className="bg-green-50 border border-green-100 p-4 rounded-lg mb-4">
              <p className="text-xs font-semibold text-green-800 mb-2 uppercase tracking-wide">Deliverables</p>
              <ul className="text-sm text-green-900 space-y-1">
                <li className="flex items-start gap-2">
                  <span className="text-green-600 mt-0.5">✓</span>
                  Desktop assessment of zoning & planning controls
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 mt-0.5">✓</span>
                  Desktop analysis of site constraints
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 mt-0.5">✓</span>
                  Development potential summary
                </li>
              </ul>
            </div>
            <button
              onClick={() => setShowFeasibilityViewer(true)}
              className="w-full bg-green-600 hover:bg-green-700 text-white px-4 py-2.5 rounded-lg transition-colors text-sm font-medium"
            >
              View Sample Report
            </button>
          </div>
          
          {/* Stage 2 Card */}
          <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100 flex flex-col">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold text-green-600 bg-green-50 px-3 py-1 rounded-full uppercase tracking-wide">Stage 2</span>
              <span className="text-2xl font-bold text-slate-800">$299<span className="text-sm font-normal text-slate-500">+</span></span>
            </div>
            <h3 className="text-lg font-bold text-slate-800 mb-2">Concept Design</h3>
            <p className="text-sm text-slate-600 mb-4 leading-relaxed flex-grow">
              Creative design solutions with 3D models, visual renderings, and detailed concept plans tailored to your requirements and site constraints.
            </p>
            <div className="bg-blue-50 border border-blue-100 p-4 rounded-lg mb-4">
              <p className="text-xs font-semibold text-blue-800 mb-2 uppercase tracking-wide">Deliverables</p>
              <ul className="text-sm text-blue-900 space-y-1">
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 mt-0.5">✓</span>
                  Concept plans
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 mt-0.5">✓</span>
                  3D models & visual renderings
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 mt-0.5">✓</span>
                  Compliance tables
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600 mt-0.5">✓</span>
                  Finishes schedule
                </li>
              </ul>
            </div>
            <button
              onClick={() => setShowSampleViewer(true)}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-lg transition-colors text-sm font-medium"
            >
              View Sample Designs
            </button>
          </div>
        </div>
        
        {/* Coming Soon note */}
        <p className="text-center text-slate-500 text-sm italic mb-8">
          Stage 3 (Authority Approval Documentation) & Stage 4 (Construction Documentation) — coming soon.
        </p>
        
        {/* Pricing link */}
        <div className="text-center mb-8">
          <button
            onClick={() => setShowPricingTable(true)}
            className="text-blue-600 hover:text-blue-700 font-medium inline-flex items-center gap-2 hover:underline"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            View Full Pricing Guide
          </button>
        </div>

        {/* CTA */}
        <div className="text-center flex flex-wrap gap-4 justify-center">
          <button 
            onClick={() => { setAuthMode('register'); setShowAuthModal(true); }}
            className="bg-green-600 text-white px-10 py-4 rounded-lg font-semibold text-lg hover:bg-green-700 transition-colors shadow-lg"
          >
            Start Your Project
          </button>
          <button 
            onClick={() => { setAuthMode('login'); setShowAuthModal(true); }}
            className="bg-white border-2 border-slate-300 text-slate-700 px-10 py-4 rounded-lg font-semibold text-lg hover:bg-slate-50 transition-colors"
          >
            Login
          </button>
        </div>
      </div>
    </section>
  );

  const Footer = () => (
    <footer className="bg-slate-900 text-white py-12">
      <div className="max-w-6xl mx-auto px-8">
        <div className="border-t border-slate-700 pt-8">
          <div className="flex justify-between items-start flex-wrap gap-8">
            <div className="flex-1 max-w-3xl">
              <p className="text-[10px] text-slate-400 opacity-90 leading-relaxed">
                <strong>Disclaimer:</strong> Services limited to low-rise residential developments in NSW. Information based on government regulations and site data available at time of service delivery, subject to change. No guarantee of authority approvals. Designs are specific to project/site. Client responsible for site and other information accuracy they provide to planAIdesign. Third-party consultants contract directly with client. planAIdesign manages third party consultants on client's behalf. Liability limited to fees paid. Use constitutes acceptance of Terms of Service and Privacy Policy.
              </p>
            </div>
            <div className="flex flex-col items-end gap-2">
              <div className="flex gap-6 mb-2">
                <a 
                  href="/privacy" 
                  onClick={handlePrivacyClick}
                  className="text-slate-400 hover:text-white text-[10px] cursor-pointer"
                >
                  Privacy Policy
                </a>
                <a 
                  href="/terms" 
                  onClick={handleTermsClick}
                  className="text-slate-400 hover:text-white text-[10px] cursor-pointer"
                >
                  Terms of Service
                </a>
              </div>
              <p className="text-[10px] text-slate-400">ABN: 33 689 275 581</p>
              <p className="text-[10px] text-slate-400">Enquiries: <a href="mailto:team@planaidesign.com" className="text-slate-300 hover:text-white">team@planaidesign.com</a></p>
              <p className="text-[10px] text-slate-300">© 2025 planAIdesign Pty Ltd.</p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );

  return (
    <>
      {/* Update Banner - shows at top of page */}
      {showUpdateBanner && <UpdateBanner />}
      
      <Hero />
      <HowItWorks />
      <Services />
      <Footer />
      
      {/* Sample Viewers */}
      {showSampleViewer && (
        <SampleConceptViewer 
          isOpen={showSampleViewer} 
          onClose={() => setShowSampleViewer(false)} 
        />
      )}
      {showFeasibilityViewer && (
        <SampleFeasibilityViewer 
          isOpen={showFeasibilityViewer} 
          onClose={() => setShowFeasibilityViewer(false)} 
        />
      )}
      
      {/* Pricing Table Modal */}
      <PricingTableModal 
        isOpen={showPricingTable} 
        onClose={() => setShowPricingTable(false)} 
      />
    </>
  );
};

export default LandingPage;